import { Button, Checkbox } from "@mui/joy";
import React, {
  Dispatch,
  MutableRefObject,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { BLACK } from "../../../appConstants";
import {
  VariableSizeList as List,
  ListChildComponentProps,
} from "react-window";

type SearchDataArrProps = {
  value: string;
};
type All_CategoriesProps = {
  principal_category: SearchDataArrProps[];
  main_category: SearchDataArrProps[];
  category: SearchDataArrProps[];
  sub_category: SearchDataArrProps[];
  product_name: SearchDataArrProps[];
  style_code: SearchDataArrProps[];
  model_code: SearchDataArrProps[];
  variant_code: SearchDataArrProps[];
  sku: SearchDataArrProps[];
  platform_code: SearchDataArrProps[];
  ad_type: SearchDataArrProps[];
  campaign_name: SearchDataArrProps[];
  adgroup_name: SearchDataArrProps[];
  bcg_type: SearchDataArrProps[];
  targeting_type: SearchDataArrProps[];
  match_type: SearchDataArrProps[];
  sfr: SearchDataArrProps[];
  targeting: SearchDataArrProps[];
  placementclassification: SearchDataArrProps[];
  [key: string]: SearchDataArrProps[];  // This index signature allows dynamic access
};
type All_Sel_CategoriesProps = {
  principal_category: string[];
  main_category: string[];
  category: string[];
  sub_category: string[];
  product_name: string[];
  style_code: string[];
  model_code: string[];
  variant_code: string[];
  sku: string[];
  platform_code: string[];
  ad_type: string[];
  campaign_name: string[];
  adgroup_name: string[];
  bcg_type: string[];
  targeting_type: string[];
  match_type: string[];
  sfr: string[];
  targeting: string[];
  placementclassification: string[];
  [key: string]: string[];  // This index signature allows dynamic access
};
type MultiSelectSearchCmpProps = {
  searchcat: string;
  childrenarr: SearchDataArrProps[];
  setCategories: Dispatch<SetStateAction<All_CategoriesProps>>;
  selectedarr: string[];
  sel_categories: All_Sel_CategoriesProps;
  setSel_Categories: Dispatch<SetStateAction<All_Sel_CategoriesProps>>;
  onSelCategory: (column_name: string, sel_arr : string[]) => void;
  DropDownApplyBtn: (column_name: string) => void;
};

const MultiSelectSearchCmp: React.FC<MultiSelectSearchCmpProps> = ({
  searchcat,
  childrenarr,
  setCategories,
  selectedarr,
  sel_categories,
  setSel_Categories,
  onSelCategory,
  DropDownApplyBtn
}) => {
  // console.log('->',searchcat, childrenarr?.length);
  const [searchvalue, setSearchValue] = useState<string>("");
  const [toggle, setToggle] = useState<boolean>(false);

  const onChangeHandler = (
    e: React.ChangeEvent<HTMLInputElement>,
    id: string
  ) => {
    const updatedSet = new Set(selectedarr);
    e.target.checked ? updatedSet.add(id) : updatedSet.delete(id);

    // If `searchcat` is a valid key, update it dynamically
    if (searchcat) {
      let new_list_arr = childrenarr
      .filter((child) => updatedSet.has(child?.value))
      .map((child) => child.value);
      setSel_Categories((prev) => ({
        ...prev,
        [searchcat]: new_list_arr,
      }));
      onSelCategory(searchcat, new_list_arr);
    }
  };

  const onSearchChangeHandler = (e: any) => {
    setSearchValue(e.target.value);
    const lowerSearch = e.target.value.toLowerCase();

    setCategories((prev: any) => ({
      ...prev,
      [searchcat]: childrenarr.sort((a, b) => {
        const aValue = a.value?.toLowerCase() || "";
        const bValue = b.value?.toLowerCase() || "";

        const aIncludes = aValue.includes(lowerSearch);
        const bIncludes = bValue.includes(lowerSearch);

        if (aIncludes && !bIncludes) return -1;
        if (!aIncludes && bIncludes) return 1;
        return 0;
      }),
    }));
  };

  let menuRef: MutableRefObject<HTMLDivElement | null> = useRef(null);
  let menuButtonRef = useRef<HTMLDivElement | null>(null); // Menu button reference
  let checkboxRef = useRef<HTMLDivElement | null>(null); // Checkbox reference

  // const handleClickOutside = (event: MouseEvent) => {
  //   // If the click is inside the dropdown (menuRef), do nothing
  //   if (menuRef.current && menuRef.current.contains(event.target as Node)) {
  //     return; // Clicking inside should not close the dropdown
  //   }
  
  //   // If the click is inside the button that opens the menu, do nothing
  //   if (menuButtonRef.current && menuButtonRef.current.contains(event.target as Node)) {
  //     return; // Clicking the button should not close the dropdown
  //   }
  
  //   // Close dropdown when clicking outside
  //   setSearchValue("");
  //   setToggle(false);
  // };

  const handleClickOutside = (event: MouseEvent) => {
    // Check if the click is outside the dropdown (menuRef)
    if (
      menuRef.current &&
      !menuRef.current.contains(event.target as Node) &&
      menuButtonRef.current &&
      !menuButtonRef.current.contains(event.target as Node)
    ) {
      setSearchValue("");
      setSel_Categories(sel_categories);
      setToggle(false); // Close the dropdown if clicked outside
    }
  };

  useEffect(() => {
    // Attach the click event listener to the document
    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  const listRef = useRef<List>(null); // Reference for VariableSizeList
  const getItemSize = (index: number) => {
    let text = "";
    if (Array.isArray(childrenarr) && childrenarr[index] && childrenarr[index].value != null) {
        text = String(childrenarr[index]?.value);
    }
    const baseHeight = 20; // Minimum height for one line of text
    const charPerLine = Math.floor(134 / 8.2); // Approx. characters per line at 14px, weight 600
    const extraLines = Math.ceil(text.length / charPerLine) - 1; // Calculate additional lines
  
    return baseHeight + Math.max(0, extraLines) * 20 + 32; // Each extra line adds 20px
  };  

  const Row = ({
    index,
    style,
    data,
  }: ListChildComponentProps<SearchDataArrProps[]>) => {
    const child = data[index]; // Get the correct item
    return (
      <div key={String(child?.value)} style={style} className="flex items-center p-2 w-full">
        <Checkbox
          ref={checkboxRef}
          size="sm"
          onChange={(e) => {
            e.stopPropagation();
            onChangeHandler(e, child?.value);}
          }
          checked={selectedarr?.includes(child?.value)}
          data-ignore-click="true"
        />
        <span
          className="ml-1"
          style={{
            fontSize: 10,
            fontFamily: "Inter",
            fontWeight: selectedarr?.includes(child?.value) ? 600 : 400,
            lineHeight: "14px",
            color: "#1C1C1E",
            width: "100%",
            textWrap: "wrap",
            wordBreak: "break-word",
          }}
        >
          {String(child?.value)}
        </span>
      </div>
    );
  };

  return (
    <>
      <div
        className="cursor-pointer"
        onClick={() =>{ setToggle((prev) => !prev);}}
        ref={menuButtonRef}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
        >
          <path
            d="M8.83758 13.1051L8.83759 13.1051C8.95485 12.9878 9.10002 12.9292 9.27396 12.9292H10.8846C11.0585 12.9292 11.2038 12.9879 11.3212 13.1053C11.4384 13.2227 11.4971 13.3679 11.4971 13.5419C11.4971 13.716 11.4384 13.8611 11.3212 13.9782C11.2038 14.0955 11.0585 14.1542 10.8846 14.1542H9.27396C9.10003 14.1542 8.95486 14.0954 8.83759 13.978C8.72019 13.8606 8.66146 13.7154 8.66146 13.5415C8.66146 13.3674 8.72019 13.2222 8.83758 13.1051ZM3.18864 6.06137L3.18863 6.06137C3.07123 5.94397 3.0125 5.79872 3.0125 5.62479C3.0125 5.45072 3.07123 5.30555 3.18862 5.18843L3.18863 5.18842C3.30589 5.07116 3.45106 5.0125 3.625 5.0125H16.5417C16.7156 5.0125 16.8608 5.07123 16.978 5.18863L16.978 5.18863C17.0954 5.30603 17.1542 5.45128 17.1542 5.62521C17.1542 5.79928 17.0954 5.94445 16.978 6.06157L16.978 6.06158C16.8608 6.17884 16.7156 6.2375 16.5417 6.2375H3.625C3.45107 6.2375 3.3059 6.17877 3.18864 6.06137ZM6.04479 10.1958C5.87086 10.1958 5.72569 10.1371 5.60843 10.0197C5.49116 9.90231 5.4325 9.75706 5.4325 9.58313C5.4325 9.40905 5.49116 9.26388 5.60842 9.14676L5.60842 9.14676C5.72568 9.02949 5.87085 8.97083 6.04479 8.97083H14.1138C14.2877 8.97083 14.4329 9.02956 14.5503 9.14696C14.6676 9.26436 14.7263 9.4096 14.7263 9.58354C14.7263 9.75762 14.6676 9.90279 14.5503 10.0199C14.4329 10.1372 14.2877 10.1958 14.1138 10.1958H6.04479Z"
            fill="#97979A"
            stroke="#97979A"
            stroke-width="0.025"
          />
        </svg>
      </div>
      {toggle && (
        <div
          className="flex flex-col items-start"
          style={{
            position: "absolute",
            top: "34px",
            height: 240,
            right: 0,
            backgroundColor: "#FFF",
            width: "100%",
            borderRadius: "8px",
            boxShadow: " 0px 4px 10px 4px rgba(28, 28, 30, 0.05)",
            zIndex:100
          }}
          ref={menuRef}
        >
          <div className="flex px-1 pt-1 flex-col items-start gap-1 self-stretch">
            <input
              type="text"
              name=""
              className="w-full h-[32px] px-2"
              placeholder={`Search`}
              value={searchvalue}
              onChange={onSearchChangeHandler}
              style={{
                fontSize: "10px",
                fontWeight: searchvalue ? 500 : 400,
                lineHeight: "14px",
                outline: "none",
                border: "1px solid var(--Components-Primary-Grey, #EAEAED)",
                borderRadius: "4px",
              }}
            />
          </div>

          <div className="h-[250px] py-1 flex flex-col w-full">
          <List
            ref={listRef}
            height={156} // Height of the list container
            itemCount={childrenarr.length} // Number of items
            itemSize={getItemSize} // ✅ Dynamic height function
            width={"100%"} // Full width
            itemData={childrenarr} // Data for each row
            overscanCount={10}
          >
            {(props, index) => <Row {...props} key={index} />}
          </List>
          
          <div className="h-[52px] px-1 py-1 w-full ">
            <Button
              onClick={() =>{ 
                DropDownApplyBtn(searchcat); 
                setToggle(prev => !prev);
              }}
              sx={{
                height: "36px",
                width: "100%",
                backgroundColor: "#1565C0",
                color: "#FFF",
                borderRadius: "4px",
                padding: "0px 4px",
                fontSize: "10px",
                fontWeight: 700,
                lineHeight: "16px",
              }}
              // disabled = {selectedarr?.length === 0}
              // loading={loading}
            >
              Apply
            </Button>
          </div>
          </div> 
          {/* <div
            className="flex py-2 flex-col items-start self-stretch "
            style={{
              overflowY: "auto",
            }}
          >
            {childrenarr?.map((child) => {
              return (
                <div key={child?.value} className="flex items-center px-4 py-1 w-full">
                  <Checkbox
                    size="sm"
                    onChange={(e) => onChangeHandler(e, child?.value)}
                    checked={selectedarr?.includes(child?.value)}
                    // color={checkboxcolor ? "primary" : "neutral"}
                  />
                  <span
                    className="ml-2"
                    style={{
                      fontSize: 14,
                      fontFamily:"Inter",
                      fontWeight: selectedarr?.includes(child?.value) ? 600 : 400,
                      lineHeight: "20px",
                      color: "#1C1C1E",
                      width:"100%",
                      textWrap:"wrap",
                      wordBreak:"break-word"
                    }}
                  >
                    {child?.value}
                  </span>
                </div>
              );
            })}
          </div> */}
        </div>
      )}
    </>
  );
};

export default MultiSelectSearchCmp;

